import { useEffect, useState } from 'react';

/**
 * Detects the user's primary input mode and re-evaluates it dynamically.
 *
 * Returns 'touch' or 'mouse'.
 *
 * Detection priority:
 *   1. Initial value: `(pointer: coarse)` media query → 'touch'; otherwise 'mouse'.
 *   2. After mount: the LAST observed input event wins. A `touchstart` switches to
 *      'touch'; a `mousemove` switches to 'mouse'. This handles hybrid devices
 *      (e.g., Surface tablets, iPads with Magic Keyboard) without requiring a refresh.
 *   3. Media-query changes (e.g., user attaches/detaches a stylus) trigger a re-evaluation.
 *
 * The hook intentionally does NOT key off screen width — phones with large screens,
 * touch-first kiosks, and small mouse-driven laptops are all distinguished correctly.
 */
export function useInputMode() {
  const getInitial = () => {
    if (typeof window === 'undefined') return 'mouse';
    try {
      return window.matchMedia('(pointer: coarse)').matches ? 'touch' : 'mouse';
    } catch {
      return 'mouse';
    }
  };

  const [mode, setMode] = useState(getInitial);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Debounce: only update state when the value actually changes — avoids re-renders
    // on every mousemove tick.
    let last = mode;
    const setIfChanged = (next) => {
      if (next !== last) {
        last = next;
        setMode(next);
      }
    };

    const onTouch = () => setIfChanged('touch');
    const onMouse = () => setIfChanged('mouse');

    // touchstart always wins — capture so we hear it before anything else, passive for perf
    window.addEventListener('touchstart', onTouch, { passive: true, capture: true });

    // iOS Safari + Chrome synthesize phantom `mousemove` events after every touch — listening
    // for them on coarse-pointer (touch-primary) devices makes the mode flap back to 'mouse'
    // immediately after every tap, hiding the mobile topbar and causing a "double-tap"
    // requirement to open the drawer. So we only listen to mousemove on fine-pointer devices
    // (real laptops/desktops). MQ changes (below) still handle hybrid devices reliably.
    let isCoarsePointer = false;
    try {
      isCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
    } catch { /* no-op */ }
    if (!isCoarsePointer) {
      window.addEventListener('mousemove', onMouse, { passive: true });
    }

    let mql;
    let mqlListener;
    try {
      mql = window.matchMedia('(pointer: coarse)');
      mqlListener = (e) => setIfChanged(e.matches ? 'touch' : 'mouse');
      // Modern + Safari fallback
      if (mql.addEventListener) mql.addEventListener('change', mqlListener);
      else if (mql.addListener) mql.addListener(mqlListener);
    } catch { /* no-op */ }

    return () => {
      window.removeEventListener('touchstart', onTouch, { capture: true });
      if (!isCoarsePointer) {
        window.removeEventListener('mousemove', onMouse);
      }
      if (mql && mqlListener) {
        if (mql.removeEventListener) mql.removeEventListener('change', mqlListener);
        else if (mql.removeListener) mql.removeListener(mqlListener);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return mode;
}

export default useInputMode;
